import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { styled } from "@mui/material";
import { toast } from "react-toastify";
import dayjs, { Dayjs } from "dayjs";

import {
  useCheckPermission,
  useCompareDates,
} from "../../shared/Helpers/hooks";
import { CustomButton, CustomCheckBox, Picker } from "../../shared/uiComponents";
import { dateNormalizer } from "../../shared/Helpers/functions";
import SearchSelect, { MultipleSearchSelect } from "../../shared/uiComponents/Dropdown/searchSelect";
import { PERMISSIONS } from "../../App/constants";
import { DataProperties } from "../../shared/uiComponents/Radio";

import { DispatchProperties, useSelector } from "../../redux/store";
import { getUsers } from "../../redux/State/userSlice";
import {
  getTherapists,
  getUserClients,
} from "../../redux/State/clientSlice/userClientSlice";
import { getClients } from "../../redux/State/clientSlice/clientSlice";
import {
  setClientId,
  setUserId,
  setEndDate,
  setStartDate,
  setSearchStringClient,
  setSearchStringUser,
  setOnlyNew,
  // clearFilterData,
  setTimeError,
  setClientIds,
} from "../../redux/State/clientSlice/billingSlice";

const today = dayjs();
const startDayOfMonth = today.startOf("month");
const beginningOfLastMoth = startDayOfMonth.subtract(1, "month");
const endOfLastMoth = beginningOfLastMoth.endOf("month");
const startOfYear = today.startOf("year");

export const ReportFilters: FC<{
  showClients?: boolean;
  showUsers: boolean;
  showShorcuts?: boolean;
  showOnlyNew?: boolean;
  isMultipleSelect?: boolean;
}> = ({
  showUsers,
  showClients = true,
  showShorcuts = true,
  showOnlyNew = true,
  isMultipleSelect = false,
}) => {
    const dispatch = useDispatch<DispatchProperties>();

    const CLIENT = useCheckPermission(PERMISSIONS.CLIENT.READ);
    const USER = useCheckPermission(PERMISSIONS.USER.READ);

    const { clientId, userId, onlyNew } = useSelector((state) => state.billing.billingFilters);
    const loadingUsers = useSelector((state) => state.user.loading);
    const loadingClients = useSelector((state) => state.client.loading);
    const loadingBilling = useSelector((state) => state.billing.loading);
    const loadingExport = useSelector((state) => state.export.loading);

    const [startDateValue, setStartDateValue] = useState<Dayjs>(startDayOfMonth);
    const [endDateValue, setEndDateValue] = useState<Dayjs>(today);

    const searchClient = useSelector(state => state.billing.billingFilters.clientSearch);
    const searchUser = useSelector(state => state.billing.billingFilters.userSearch);
    const clientIds = useSelector((state) => state.billing.billingFilters.clientIds);

    const [pageSizeUser, setPageSizeUser] = useState<number>(8);
    const [pageSizeClient, setPageSizeClient] = useState<number>(8);
    const [usersData, setUsersData] = useState<Array<DataProperties>>([]);
    const [clientsData, setClientsData] = useState<Array<DataProperties>>([]);
    const [hasMore, setHasMore] = useState<boolean | undefined>(false);

    const { hasError, message } = useCompareDates(startDateValue, endDateValue);

    // useEffect(() => {
    //   //clearing previously entered data for filters on new page
    //   dispatch(clearFilterData())
    // }, [dispatch])

    useEffect(() => {
      dispatch(setTimeError(hasError));
    }, [dispatch, hasError])

    useEffect(() => {
      if (!startDateValue || !endDateValue) return;
      const start = dateNormalizer(startDateValue);
      const end = dateNormalizer(endDateValue);
      dispatch(setStartDate(start));
      dispatch(setEndDate(end));
    }, [startDateValue, endDateValue, dispatch]);

    useEffect(() => {
      if (
        !pageSizeUser ||
        !!clientId ||
        !USER.permissionGranted ||
        !showUsers
      )
        return;
      const timeout = setTimeout(() => {
        dispatch(
          getUsers({
            page: "1",
            pageSize: `${pageSizeUser}`,
            searchString: searchUser,
          })
        )
          .then(unwrapResult)
          .then((response) => {
            if (!response.query) return;
            const data: Array<DataProperties> = response.query.map((user) => ({
              id: user.id,
              label: user.fullName,
            }));
            setUsersData(data);
          });
      }, 1000);
      return () => clearTimeout(timeout);
    }, [
      dispatch,
      pageSizeUser,
      searchUser,
      clientId,
      USER.permissionGranted,
      showUsers,
    ]);

    useEffect(() => {
      if (!clientId || !USER.permissionGranted || !showUsers) return;

      const timeout = setTimeout(() => {
        dispatch(getTherapists({ clientId }))
          .then(unwrapResult)
          .then((response) => {
            if (!response) return;
            const data: Array<DataProperties> = response.map((user) => ({
              id: user.id,
              label: user.fullName,
            }));
            setUsersData(data);
          });
      }, 1000);
      return () => clearTimeout(timeout);
    }, [dispatch, clientId, USER.permissionGranted, showUsers]);

    useEffect(() => {
      if (!pageSizeClient || !CLIENT.permissionGranted || !showClients) return;

      if (!userId) {
        const timeout = setTimeout(() => {
          dispatch(
            getClients({
              page: "1",
              pageSize: `${pageSizeClient}`,
              searchString: searchClient,
            })
          )
            .then(unwrapResult)
            .then((response) => {
              if (!response.query) return;
              const data: Array<DataProperties> = response.query.map(
                (client) => ({
                  id: client.id,
                  label: client.fullName,
                })
              );
              setClientsData(data);
              setHasMore(response.hasNextPage)
            });
        }, 1000);
        return () => clearTimeout(timeout);
      }

      const timeout = setTimeout(() => {
        dispatch(
          getUserClients({
            userId: userId,
            page: "1",
            pageSize: `${pageSizeClient}`,
            searchString: searchClient,
          })
        )
          .then(unwrapResult)
          .then((response) => {
            if (!response.query) return;
            const data: Array<DataProperties> = response.query.map((client) => ({
              id: client.id,
              label: client.fullName,
            }));
            setClientsData(data);
            setHasMore(response.hasNextPage)
          });
      }, 1000);
      return () => clearTimeout(timeout);
    }, [
      dispatch,
      pageSizeClient,
      searchClient,
      userId,
      CLIENT.permissionGranted,
      showClients,
    ]);

    const startDateHandler = (date: Dayjs | null) => {
      if (!date) return;
      setStartDateValue(date);
    };

    const endDateHandler = (date: Dayjs | null) => {
      if (!date) return;
      setEndDateValue(date);
    };

    const userHandler = (value: string) => {
      if (!value) return;
      dispatch(setUserId(value));
    };

    const clientHandler = (value: string) => {
      if (!value) return;
      dispatch(setClientId(value));
    };

    const userSearchStringHandler = (value: string) => {
      dispatch(setSearchStringUser(value));
    };

    const clientSearchStringHandler = (value: string) => {
      dispatch(setSearchStringClient(value));
    };

    const onlyNewHandler = (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      dispatch(setOnlyNew(checked));
    };

    const thisMonthHandler = () => {
      setStartDateValue(startDayOfMonth);
      setEndDateValue(today);
    };

    const lastMonthHandler = () => {
      setStartDateValue(beginningOfLastMoth);
      setEndDateValue(endOfLastMoth);
    };

    const thisYearHandler = () => {
      setStartDateValue(startOfYear);
      setEndDateValue(today);
    };

    const onClientIdsChange = (ids: Array<string>) => {
      if (!ids) return;
      dispatch(setClientIds(ids))
    };

    return (
      <>
        <TimeInputsWrapper>
          {CLIENT.permissionGranted && showClients && (
            <div style={{ width: "300px" }}>
              {isMultipleSelect ? (
                <MultipleSearchSelect
                  label="Find Clients:"
                  values={clientIds}
                  data={clientsData}
                  setValues={onClientIdsChange}
                  searchString={searchClient}
                  setSearchString={clientSearchStringHandler}
                  loading={loadingExport || loadingClients || loadingBilling}
                  hasMore={hasMore}
                  setPageSize={setPageSizeClient}
                />
              ) : (
                <SearchSelect
                  label="Find Client:"
                  searchString={searchClient}
                  setSearchString={clientSearchStringHandler}
                  data={clientsData}
                  setValue={clientHandler}
                  loadMore={{
                    activate: true,
                    setSize: setPageSizeClient,
                  }}
                  loading={loadingExport || loadingClients || loadingBilling}
                  clearData={() => dispatch(setClientId(""))}
                />)
              }
            </div>
          )}
          {USER.permissionGranted && showUsers && (
            <div style={{ width: "250px" }}>
              <SearchSelect
                label="Find Provider:"
                searchString={searchUser}
                setSearchString={userSearchStringHandler}
                data={usersData.filter((x) =>
                  !!clientId
                    ? x.label?.toLowerCase().includes(searchUser.toLowerCase())
                    : true
                )}
                setValue={userHandler}
                loadMore={{
                  activate: true,
                  setSize: setPageSizeUser,
                }}
                loading={loadingUsers || loadingBilling}
                clearData={() => dispatch(setUserId(""))}
              />
            </div>
          )}
          <Picker.CustomDate
            label="Start Date"
            value={startDateValue}
            onChange={startDateHandler}
            disabled={loadingBilling}
          />
          <Picker.CustomDate
            label="End Date"
            error={hasError}
            errorMessage={message}
            value={endDateValue}
            onChange={endDateHandler}
            disabled={loadingBilling}
          />
        </TimeInputsWrapper>

        {showShorcuts && (
          <TimeInputsWrapper>
            {
              showOnlyNew && <CustomCheckBox
                item={{
                  label: "Show only new",
                  id: "0",
                  checked: onlyNew,
                  disabled: loadingBilling,
                }}
                onChange={onlyNewHandler}
              />
            }
            <CustomButton
              title="This month"
              onClick={thisMonthHandler}
              secondaryButton
              disabled={loadingBilling}
            />
            <CustomButton
              title="Last month"
              onClick={lastMonthHandler}
              secondaryButton
              disabled={loadingBilling}
            />
            <CustomButton
              title="This year"
              onClick={thisYearHandler}
              secondaryButton
              disabled={loadingBilling}
            />
          </TimeInputsWrapper>
        )}
      </>
    );
  };

export const TimeInputsWrapper = styled("div")(() => ({
  display: "flex",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "16px",
  "@media (max-width: 525px)": {
    flexDirection: "column",
    "&>div": {
      marginBottom: "16px",
    },
  },
}));

export const downloadFile = ({
  file,
  fileName,
}: {
  file: Blob;
  fileName: string;
}) => {
  const objectURL = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = objectURL;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  toast("Downloaded");
};
